'use client';

import { Link } from '@/components/link';
import { formatCurrency } from '@/lib/currency';
import { getGiftCardAmountPrice } from '@/lib/gift-card-product-utils';
import { cn } from '@/lib/utils';
import type { ShopperProducts } from '@/scapi';
import { type KeyboardEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type GiftCardAmountValue = {
    name: string;
    value: string;
    orderable?: boolean;
    href: string;
    selected: boolean;
};

const RADIO_NAV_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

function isEnabledRadio(element: HTMLElement): boolean {
    return element.getAttribute('aria-disabled') !== 'true' && !element.hasAttribute('disabled');
}

/**
 * Roving tabindex + selection, matching the ARIA radio pattern:
 * arrows move to the next enabled cut, wrap, and activate it.
 */
function onAmountGroupKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const target = event.target;
    if (event.key === ' ' && target instanceof HTMLAnchorElement && target.getAttribute('role') === 'radio') {
        event.preventDefault();
        target.click();
        return;
    }

    if (!RADIO_NAV_KEYS.has(event.key)) {
        return;
    }

    const radios = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')).filter(
        isEnabledRadio
    );
    if (radios.length === 0) {
        return;
    }

    event.preventDefault();

    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const activeElement = event.target instanceof Element ? event.target.closest('[role="radio"]') : null;
    const activeIndex = activeElement instanceof HTMLElement ? radios.indexOf(activeElement) : -1;
    const checkedIndex = radios.findIndex((radio) => radio.getAttribute('aria-checked') === 'true');
    const currentIndex = activeIndex === -1 ? Math.max(checkedIndex, 0) : activeIndex;
    const next = radios[(currentIndex + direction + radios.length) % radios.length];

    if (!next || next === activeElement) {
        return;
    }

    next.focus();
    next.click();
}

type GiftCardAmountSelectorProps = {
    values: GiftCardAmountValue[];
    product: ShopperProducts.schemas['Product'];
    currency: string;
    /** Currently selected cut value (controlled or from URL). */
    selectedValue?: string;
    /** When set, tiles use callbacks instead of URL navigation. */
    onSelect?: (value: string) => void;
    /** When true, only the selected tile is shown and interaction is disabled. */
    readOnly?: boolean;
};

/**
 * Equal-width gift card denomination tiles (Figma amount cut selector).
 * Uncontrolled: navigates via Link hrefs. Controlled: calls onSelect.
 */
export default function GiftCardAmountSelector({
    values,
    product,
    currency,
    selectedValue,
    onSelect,
    readOnly = false,
}: GiftCardAmountSelectorProps): ReactElement | null {
    const { i18n, t } = useTranslation('product');
    const tiles = readOnly ? values.filter((value) => value.value === selectedValue) : values;
    const enabledTiles = tiles.filter((tile) => !(readOnly || tile.orderable === false));
    const selectedEnabled = enabledTiles.find((tile) => selectedValue === tile.value || tile.selected);
    const tabStopValue = selectedEnabled?.value ?? enabledTiles[0]?.value;

    if (tiles.length === 0) {
        return null;
    }

    return (
        <div
            className="flex w-full min-w-0 gap-2"
            role="radiogroup"
            aria-orientation="horizontal"
            aria-label={t('giftCard.amountSelectorAriaLabel')}
            data-testid="gift-card-amount-selector"
            onKeyDown={onAmountGroupKeyDown}>
            {tiles.map((tile) => {
                const amount = getGiftCardAmountPrice(product, tile.value);
                const label =
                    amount != null ? formatCurrency(amount, i18n.language, currency) : tile.name || tile.value;
                const isSelected = selectedValue === tile.value || tile.selected;
                const isDisabled = readOnly || tile.orderable === false;
                const tabIndex = !isDisabled && tile.value === tabStopValue ? 0 : -1;
                const tileClassName = cn(
                    'flex flex-1 basis-0 min-w-0 flex-col items-center justify-center rounded p-4 border border-solid body-copy-l-bold text-neutral-10 text-center whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isSelected ? 'border-brown-20' : 'border-neutral-80',
                    isDisabled && !isSelected && 'opacity-50 cursor-not-allowed',
                    !isDisabled && 'cursor-pointer'
                );

                if (onSelect) {
                    return (
                        <button
                            key={tile.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            aria-label={label}
                            tabIndex={tabIndex}
                            disabled={isDisabled}
                            data-testid={`gift-card-amount-${tile.value}`}
                            data-selected={isSelected || undefined}
                            className={tileClassName}
                            onClick={() => {
                                if (!isDisabled) {
                                    onSelect(tile.value);
                                }
                            }}>
                            {label}
                        </button>
                    );
                }

                if (isDisabled) {
                    return (
                        <span
                            key={tile.value}
                            role="radio"
                            aria-checked={isSelected}
                            aria-disabled="true"
                            aria-label={label}
                            tabIndex={-1}
                            data-testid={`gift-card-amount-${tile.value}`}
                            data-selected={isSelected || undefined}
                            className={tileClassName}>
                            {label}
                        </span>
                    );
                }

                return (
                    <Link
                        key={tile.value}
                        to={tile.href}
                        preventScrollReset
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={label}
                        tabIndex={tabIndex}
                        data-testid={`gift-card-amount-${tile.value}`}
                        data-selected={isSelected || undefined}
                        className={tileClassName}>
                        {label}
                    </Link>
                );
            })}
        </div>
    );
}
